import { useState, useEffect, useCallback } from 'react'
import { api } from '../api'
import { riskPill, actionPill } from './Pill'
import { ErrorState, errorMessage } from './ErrorState'
import { enumLabel, useI18n } from '../i18n'
import { COUNTRY_CODES, scoreColor } from '../types'
import type { CrawlReportSummary, PaginatedResponse, RiskLevel, CountryCode } from '../types'


// ── Composant ────────────────────────────────────────────────────────────────

interface Props {
  onSelectReport: (id: string) => void
}

export function ReportsTable({ onSelectReport }: Props) {
  const { t, fmt } = useI18n()
  const [data, setData] = useState<PaginatedResponse<CrawlReportSummary> | null>(null)
  const [page, setPage] = useState(1)
  const [level, setLevel] = useState<RiskLevel | ''>('')
  const [country, setCountry] = useState<CountryCode | ''>('')
  const [sortBy, setSortBy] = useState<'crawledAt' | 'riskScore'>('crawledAt')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const doFetch = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.getReports({
        page,
        limit: 15,
        level: level || undefined,
        country: country || undefined,
        sortBy,
        sortOrder: 'desc',
      })
      setData(res)
      setError(null)
    } catch (err: unknown) {
      setData(null)
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [page, level, country, sortBy])

  useEffect(() => {
    doFetch()
  }, [doFetch])

  // Reset page quand les filtres changent
  useEffect(() => {
    setPage(1)
  }, [level, country, sortBy])

  return (
    <div className="table-card">
      {/* ── Header + filtres ─────────────────────────── */}
      <div className="table-header">
        <h2>{t('reports.latest')}</h2>
        <div className="filter-row">
          <select value={level} onChange={(e) => setLevel(e.target.value as RiskLevel | '')}>
            <option value="">{t('reports.allLevels')}</option>
            <option value="CRITICAL">{t('risk.CRITICAL')}</option>
            <option value="WARNING">{t('risk.WARNING')}</option>
            <option value="OK">{t('risk.OK')}</option>
          </select>
          <select value={country} onChange={(e) => setCountry(e.target.value as CountryCode | '')}>
            <option value="">{t('reports.allCountries')}</option>
            {COUNTRY_CODES.map((c) => (
              <option key={c} value={c}>{c} {enumLabel(t, 'country', c, c)}</option>
            ))}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'crawledAt' | 'riskScore')}>
            <option value="crawledAt">{t('reports.sortRecent')}</option>
            <option value="riskScore">{t('reports.sortScore')}</option>
          </select>
        </div>
      </div>

      {error && <ErrorState message={error} onRetry={doFetch} compact />}

      {/* ── Tableau ──────────────────────────────────── */}
      <div className="table-scroll" hidden={!!error}>
        <table>
          <thead>
            <tr>
              <th scope="col">{t('common.merchant')}</th>
              <th scope="col">{t('common.country')}</th>
              <th scope="col">{t('common.score')}</th>
              <th scope="col">{t('reports.col.level')}</th>
              <th scope="col">{t('common.actions')}</th>
              <th scope="col">{t('reports.col.crawled')}</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr>
                <td colSpan={7} className="table-empty">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {data && data.data.length === 0 && (
              <tr>
                <td colSpan={7} className="table-empty">
                  {t('reports.none')}
                </td>
              </tr>
            )}
            {data?.data.map((r) => (
              <tr key={r.id} onClick={() => onSelectReport(r.id)} className="table-row-clickable">
                <td>
                  <div className="merchant-name">{r.merchantName}</div>
                  <div className="merchant-meta">
                    {enumLabel(t, 'category', r.category, r.category)} · HTTP {r.httpStatus} · SSL {r.sslValid ? '✓' : '✗'}
                  </div>
                </td>
                <td>
                  {r.countryFlag} {r.country}
                </td>
                <td>
                  <div className="score-bar">
                    <div className="score-track">
                      <div
                        className="score-fill"
                        style={{ '--progress': r.riskScore / 100, background: scoreColor(r.riskScore) } as React.CSSProperties}
                      />
                    </div>
                    <span className="score-num" style={{ color: scoreColor(r.riskScore) }}>
                      {fmt.number(r.riskScore)}
                    </span>
                  </div>
                </td>
                <td>{riskPill(r.riskLevel)}</td>
                <td>{actionPill(r.action)}</td>
                <td className="td-muted">{r.crawledAgo}</td>
                <td>
                  <button
                    className="btn-icon"
                    title={t('reports.viewDetail')}
                    aria-label={t('reports.viewDetailOf', { name: r.merchantName })}
                    onClick={(e) => {
                      e.stopPropagation()
                      onSelectReport(r.id)
                    }}
                  >
                    →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ───────────────────────────────── */}
      {data && data.totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ← {t('common.previous')}
          </button>
          <span className="page-info">
            {t('common.page', { page: data.page, pages: data.totalPages })}{' '}
            ({t('reports.resultCount', { n: data.total })})
          </span>
          <button disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>
            {t('common.next')} →
          </button>
        </div>
      )}
    </div>
  )
}
